import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CForm, CModal, CModalBody, CButton, CRow, CCol, CModalHeader } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { callApi } from '../../../apiCaller';
import { toastError } from '../../../utils';
import { API_SUBSCRIBER_USER_DETAIL } from '../../../constants';

import { iconEye, iconEyeSlash } from '../../../assets/icons/custom';
import { Waiting } from '../Loadings';
import { SimpleInputGroup } from '../Forms';

import { setIsWaiting } from '../../../actions/general';
import { addProfileUser } from '../../CMS/Subscriber/AccountSettings/actions';
import { setSubscriberUser } from '../../CMS/Subscriber/GlobalStates/CurrentUser/actions';

const validationSchema = (setValidateOnChange) => {
     setValidateOnChange(true);

     return Yup.object().shape({
          firstName: Yup.string().required('Required.'),
          lastName: Yup.string().required('Required.'),
          password: Yup.string().required('Required.').min(8, 'Password must be more than 8 characters.'),
          passwordRepeat: Yup.string()
               .required('Required.')
               .min(8, 'Password must be at least 8 characters.')
               .oneOf([Yup.ref('password'), null], 'Passwords must match.'),
     });
};

const ConfirmPasswordAndFullNamePopup = () => {
     const dispatch = useDispatch();

     const user = useSelector((state) => state.subscriber.user);
     const profileUser = useSelector((state) => state.subscriber.profileUser);
     const isWaiting = useSelector((state) => state.general.isWaiting);

     const [validateOnChange, setValidateOnChange] = useState(false);
     const [showPassword, setShowPassword] = useState(false);
     const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

     const initialValues = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          password: '',
          passwordRepeat: '',
     };

     const handleSubmitForm = (values, { setSubmitting }) => {
          const userId = user?.id;
          if (!userId) return;

          const payload = {
               firstName: values.firstName,
               lastName: values.lastName,
               password: values.password,
               firstLogin: true,
          };

          dispatch(setIsWaiting(true));
          callApi(API_SUBSCRIBER_USER_DETAIL.replace(':id', userId), 'PUT', payload)
               .then((response) => {
                    setSubmitting(false);

                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    const { firstName, lastName, email, firstLogin, country, timezone } = response.data;
                    const newUserPayload = { email, firstName, lastName, firstLogin, country, timezone };

                    dispatch(addProfileUser({ ...profileUser, ...newUserPayload }));
                    dispatch(setSubscriberUser({ ...user, ...newUserPayload }));

                    toast.success('Update successfully');
               })
               .finally(() => {
                    dispatch(setIsWaiting(false));
               });
     };

     const handleShowPassword = () => {
          setShowPassword(!showPassword);
     };

     const handleShowPasswordConfirm = () => {
          setShowPasswordConfirm(!showPasswordConfirm);
     };

     return (
          <CModal alignment="center" visible className="modal-register confirm-fullname-and-password-popup">
               <CModalHeader className="d-flex flex-column modal-header">
                    <h4 className="modal-title" id="exampleModalLongTitle">
                         Welcome to CAMS-care
                    </h4>
                    <p className="modal-title" id="exampleModalLongTitle">
                         Provide us with your full name and a new password - the one you’ve used is a one-time, temporary password. Remember to save
                         your new password!
                    </p>
               </CModalHeader>

               <CModalBody>
                    <Formik
                         initialValues={initialValues}
                         validationSchema={() => validationSchema(setValidateOnChange)}
                         onSubmit={handleSubmitForm}
                         validateOnChange={validateOnChange}
                    >
                         {({ values, errors, handleChange, handleSubmit, isSubmitting }) => {
                              return (
                                   <CForm noValidate onSubmit={handleSubmit}>
                                        <CRow>
                                             <CCol md={6}>
                                                  <SimpleInputGroup
                                                       id="firstName"
                                                       placeholder="First Name"
                                                       handleChange={handleChange}
                                                       value={values.firstName}
                                                       error={errors.firstName}
                                                       disabled={isSubmitting}
                                                       isRequired
                                                  />
                                             </CCol>
                                             <CCol md={6}>
                                                  <SimpleInputGroup
                                                       id="lastName"
                                                       placeholder="Last Name"
                                                       handleChange={handleChange}
                                                       value={values.lastName}
                                                       error={errors.lastName}
                                                       disabled={isSubmitting}
                                                       isRequired
                                                  />
                                             </CCol>
                                        </CRow>
                                        <div className="position-relative">
                                             <SimpleInputGroup
                                                  id="password"
                                                  className="input-repeat"
                                                  inputType={showPassword ? 'text' : 'password'}
                                                  placeholder="Password"
                                                  handleChange={handleChange}
                                                  value={values.password}
                                                  error={errors.password}
                                                  disabled={isSubmitting}
                                                  isRequired
                                             />
                                             <CIcon
                                                  icon={!showPassword ? iconEyeSlash : iconEye}
                                                  size="xl"
                                                  className={`icon-eye repeat-password${errors.password ? ' repeat-validate' : ''}`}
                                                  onClick={handleShowPassword}
                                             />
                                        </div>
                                        <div className="position-relative">
                                             <SimpleInputGroup
                                                  id="passwordRepeat"
                                                  className="input-repeat"
                                                  inputType={showPasswordConfirm ? 'text' : 'password'}
                                                  placeholder="Confirm password"
                                                  handleChange={handleChange}
                                                  value={values.passwordRepeat}
                                                  error={errors.passwordRepeat}
                                                  disabled={isSubmitting}
                                                  isRequired
                                             />
                                             <CIcon
                                                  icon={!showPasswordConfirm ? iconEyeSlash : iconEye}
                                                  size="xl"
                                                  className={`icon-eye repeat-password${errors.passwordRepeat ? ' repeat-validate' : ''}`}
                                                  onClick={handleShowPasswordConfirm}
                                             />
                                        </div>
                                        <div className="text-center">
                                             <CButton
                                                  type="submit"
                                                  className="btn btn-primary text-uppercase btn-blue-highlight btn-round-9 btn-h-50"
                                                  disabled={isWaiting}
                                                  onClick={handleSubmit}
                                             >
                                                  <Waiting isLoading={isWaiting}>Complete Registration</Waiting>
                                             </CButton>
                                        </div>
                                   </CForm>
                              );
                         }}
                    </Formik>
               </CModalBody>
          </CModal>
     );
};

ConfirmPasswordAndFullNamePopup.defaultProps = {};

export default ConfirmPasswordAndFullNamePopup;
